import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatMoney, formatPercent } from '@/utils/format';
import { cn } from '@/utils/cn';

import { LandingReveal } from './landing-shared';

const PAID_PLANS = ['essential', 'professional', 'enterprise'] as const;
const PAID_PLAN_FEATURE_KEYS = ['0', '1', '2', '3', '4'] as const;

type BillingInterval = 'monthly' | 'yearly';
type PaidPlan = 'essential' | 'professional';

const LANDING_BILLING_CURRENCY = 'GHS';
const YEARLY_BILLING_DISCOUNT = 0.2;

const PAID_PLAN_BILLING: Record<
  PaidPlan,
  { monthlyPerSeatMajor: number; yearlyPerSeatMajor: number; includedSeats: number }
> = {
  essential: {
    monthlyPerSeatMajor: 50,
    yearlyPerSeatMajor: 50 * (1 - YEARLY_BILLING_DISCOUNT),
    includedSeats: 8,
  },
  professional: {
    monthlyPerSeatMajor: 100,
    yearlyPerSeatMajor: 100 * (1 - YEARLY_BILLING_DISCOUNT),
    includedSeats: 15,
  },
};

const toMinor = (major: number): number => Math.round(major * 100);

const isPerSeatPlan = (plan: (typeof PAID_PLANS)[number]): plan is PaidPlan =>
  plan === 'essential' || plan === 'professional';

type LandingPaidPlansProps = {
  signedIn: boolean;
  yearly: boolean;
  onYearlyChange: (yearly: boolean) => void;
};

const PaidPlanCard = ({
  plan,
  interval,
  signedIn,
}: {
  plan: (typeof PAID_PLANS)[number];
  interval: BillingInterval;
  signedIn: boolean;
}) => {
  const { t } = useTranslation('landing');
  const perSeatBilling = isPerSeatPlan(plan) ? PAID_PLAN_BILLING[plan] : null;
  const perSeatMajor = perSeatBilling
    ? interval === 'monthly'
      ? perSeatBilling.monthlyPerSeatMajor
      : perSeatBilling.yearlyPerSeatMajor
    : null;
  const featured = plan === 'professional';

  return (
    <article
      className={cn(
        'landing-paid-plan-card relative flex h-full flex-col rounded-2xl border bg-surface p-6 text-fg sm:p-8',
        featured ? 'border-brand ring-2 ring-brand/15' : 'border-border',
      )}
    >
      {featured ? (
        <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white bg-brand px-3 py-0.5 text-caption font-semibold text-on-brand">
          {t('pricing.popular')}
        </span>
      ) : null}

      <div>
        <h3 className="text-card-title font-semibold text-fg">{t(`pricing.plans.${plan}.name`)}</h3>
        <p className="mt-3 text-pretty text-body leading-relaxed text-fg-secondary">
          {t(`pricing.plans.${plan}.body`)}
        </p>
      </div>

      <div className="mt-6 space-y-1 border-b border-border pb-6">
        <p className="text-section-title font-semibold text-brand">
          {perSeatMajor !== null
            ? formatMoney(toMinor(perSeatMajor), LANDING_BILLING_CURRENCY)
            : t(`pricing.plans.${plan}.price.${interval}`)}
        </p>
        <p className="text-body-sm text-fg-secondary">{t(`pricing.plans.${plan}.period.${interval}`)}</p>
      </div>

      <ul className="mt-6 grid flex-1 gap-3">
        {PAID_PLAN_FEATURE_KEYS.map((featureKey) => (
          <li key={featureKey} className="flex gap-3 text-body-sm leading-snug text-fg-secondary">
            <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
            <span>{t(`pricing.plans.${plan}.features.${featureKey}`)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">
        {signedIn ? (
          <Button className="w-full" variant="outline" size="lg" asChild>
            <Link to="/home">{t('nav.dashboard')}</Link>
          </Button>
        ) : (
          <Button className="w-full" variant={featured ? 'primary' : 'outline'} size="lg" asChild>
            <Link to="/register">{t(`pricing.plans.${plan}.cta`)}</Link>
          </Button>
        )}
      </div>
    </article>
  );
};

export const LandingPaidPlans = ({ signedIn, yearly, onYearlyChange }: LandingPaidPlansProps) => {
  const { t } = useTranslation('landing');
  const interval: BillingInterval = yearly ? 'yearly' : 'monthly';
  const maxYearlySavings = YEARLY_BILLING_DISCOUNT;

  return (
    <LandingReveal className="mx-auto mt-10 max-w-6xl">
      <div
        className="flex flex-col items-center gap-2"
        role="group"
        aria-label={t('pricing.billingToggleAria')}
      >
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-3">
          <span
            className={cn(
              'text-body-sm font-medium transition-colors',
              !yearly ? 'text-on-brand' : 'text-on-brand/60',
            )}
          >
            {t('pricing.billingMonthly')}
          </span>
          <Switch checked={yearly} onCheckedChange={onYearlyChange} aria-label={t('pricing.billingToggleAria')} />
          <span
            className={cn(
              'flex flex-wrap items-center justify-center gap-2 text-body-sm font-medium transition-colors',
              yearly ? 'text-on-brand' : 'text-on-brand/60',
            )}
          >
            {t('pricing.billingYearly')}
            <span className="rounded-full border border-on-brand/25 bg-on-brand/15 px-2 py-0.5 text-caption font-semibold text-on-brand">
              {t('pricing.savePercent', { percent: formatPercent(maxYearlySavings) })}
            </span>
          </span>
        </div>
      </div>

      <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {PAID_PLANS.map((plan) => (
          <li key={plan} className="min-w-0">
            <PaidPlanCard plan={plan} interval={interval} signedIn={signedIn} />
          </li>
        ))}
      </ul>

      <p className="mx-auto mt-8 max-w-2xl text-center text-body-sm text-on-brand/85">{t('pricing.note')}</p>
    </LandingReveal>
  );
};
